import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { type Bereich } from "@jornado/shared";
import { api, fehlertext } from "./api";
import { beschriftung, zielbereich, type Pruefpunkt } from "./baukasten";
import { bereichsName, useBereiche } from "./bereiche";
import { Auswahl, Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Seitenkopf, Textknopf } from "./ui";

/**
 * Die Checkliste eines Objekts.
 *
 * Oben die Bereiche, die hier gelten, darunter die Vorschau dessen, was der
 * Mitarbeiter am Objekt sehen wird. Gespeichert wird ausschliesslich die
 * Abweichung vom Standard, nie eine Kopie. Wer eine Abweichung zuruecknimmt,
 * bekommt automatisch wieder den zentralen Stand.
 */

type I18nText = Record<string, string>;

interface Rohfassung {
  checklist: {
    id: string; property_id: string; property_name: string; customer_name: string;
    label: string | null;
    bereiche: Bereich[]; interval_days: number | null; is_active: boolean;
  };
  pointOverlays: {
    catalog_id: string; action: "add" | "remove" | "override";
    target_min: string | null; target_max: string | null; photo_required: boolean | null;
    note_i18n: I18nText; key: string; label_i18n: I18nText; bereich: Bereich;
  }[];
}

interface Aufgeloest {
  bloecke: {
    bereich: Bereich; name_i18n: I18nText; gruppe_i18n: I18nText;
    points: {
      catalog_id: string; key: string; label_i18n: I18nText; note_i18n: I18nText;
      input_type: string; unit: string | null; target_min: string | null; target_max: string | null;
      photo_required: boolean; angepasst: boolean; herkunft: string;
    }[];
  }[];
}

export function ObjektChecklisteSeite({ darfAendern }: { darfAendern: boolean }) {
  const { t, i18n } = useTranslation();
  const { bereiche: alleBereiche } = useBereiche();
  const { id = "" } = useParams();
  const [roh, setRoh] = useState<Rohfassung | null>(null);
  const [vorschau, setVorschau] = useState<Aufgeloest | null>(null);
  const [allePunkte, setAllePunkte] = useState<Pruefpunkt[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [punktAnpassen, setPunktAnpassen] = useState<string | null>(null);

  function laden() {
    void api.get<Rohfassung>(`/property-checklists/${id}`).then(setRoh)
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
    void api.get<Aufgeloest>(`/property-checklists/${id}/resolved`).then(setVorschau)
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
    void api.get<{ checkpoints: Pruefpunkt[] }>("/checkpoints")
      .then((r) => setAllePunkte(r.checkpoints)).catch(() => {});
  }
  useEffect(laden, [id]);

  if (fehler && !roh) return <Fehler text={fehler} />;
  if (!roh || !vorschau) return <Laedt />;

  /*
   * Zur Wahl stehen die aktiven Bereiche und dazu jeder, der an dieser Liste
   * schon angehakt ist. Der zweite Teil ist der wichtige: wird ein Bereich
   * spaeter stillgelegt, verschwindet er sonst wortlos aus dieser Maske, und
   * niemand kann den Haken mehr wegnehmen.
   */
  const waehlbareBereiche = (alleBereiche ?? [])
    .filter((b) => b.is_active || roh.checklist.bereiche.includes(b.key));

  const abgewaehlt = roh.pointOverlays.filter((o) => o.action === "remove");
  const imBlick = new Set(vorschau.bloecke.flatMap((b) => b.points.map((p) => p.catalog_id)));

  async function bereichSchalten(bereich: Bereich, an: boolean) {
    if (!roh) return;
    const neu = an
      ? [...roh.checklist.bereiche, bereich]
      : roh.checklist.bereiche.filter((b) => b !== bereich);
    if (neu.length === 0) {
      setFehler(t("checkliste.mindestensEinBereich"));
      return;
    }
    try {
      await api.patch(`/property-checklists/${id}`, { bereiche: neu });
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function punktSchalten(catalogId: string, an: boolean) {
    try {
      if (an) await api.del(`/property-checklists/${id}/points/${catalogId}`);
      else await api.post(`/property-checklists/${id}/points`, { catalogId, action: "remove" });
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  /** Punkte aus Bereichen, die hier nicht gelten. Einzeln nachrüstbar. */
  const nachruestbar = allePunkte.filter((p) => !imBlick.has(p.id));

  /** Die Bloecke nach Bereich gebuendelt, damit jeder Bereich eine Karte wird. */
  const nachBereich = vorschau.bloecke.reduce<
    { bereich: Bereich; name_i18n: I18nText; gruppen: Aufgeloest["bloecke"] }[]
  >((liste, block) => {
    const letzter = liste[liste.length - 1];
    if (letzter && letzter.bereich === block.bereich) letzter.gruppen.push(block);
    else liste.push({ bereich: block.bereich, name_i18n: block.name_i18n, gruppen: [block] });
    return liste;
  }, []);

  return (
    <>
      <Seitenkopf
        titel={roh.checklist.customer_name}
        hinweis={[roh.checklist.property_name, roh.checklist.label ?? t("checkliste.titel")].join(" · ")}
        aktionen={
          <Link to={`/objekte/${roh.checklist.property_id}`}>
            <Knopf art="neben">{t("checkliste.zurueckZumObjekt")}</Knopf>
          </Link>
        }
      />
      <Fehler text={fehler} />

      <Karte className="mb-6">
        <h2 className="mb-1 font-bold">{t("checkliste.bereiche")}</h2>
        <p className="mb-3 text-sm text-leise">{t("checkliste.bereicheHinweis")}</p>
        {waehlbareBereiche.length === 0 ? (
          <p className="text-sm text-leise">{t("checkliste.keineBereiche")}</p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {waehlbareBereiche.map((b) => (
              <label key={b.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-5 accent-marke"
                  checked={roh.checklist.bereiche.includes(b.key)}
                  disabled={!darfAendern}
                  onChange={(e) => bereichSchalten(b.key, e.target.checked)}
                />
                <span className={`font-medium ${b.is_active ? "" : "text-leise line-through"}`}>
                  {beschriftung(b.name_i18n, i18n.language, b.key)}
                </span>
              </label>
            ))}
          </div>
        )}
      </Karte>

      <h2 className="mb-1 font-bold">{t("checkliste.vorschau")}</h2>
      <p className="mb-4 text-sm text-leise">{t("checkliste.vorschauHinweis")}</p>

      {vorschau.bloecke.length === 0 ? (
        <Karte><p className="text-sm text-leise">{t("checkliste.leer")}</p></Karte>
      ) : (
        /*
          Eine Karte je Bereich, die Zwischenueberschriften darin.
          Vorher stand jeder Block in einer eigenen Karte, und "Innen" sah
          dadurch aus wie ein eigener Bereich statt wie ein Teil von Immobilie.
        */
        <div className="space-y-4">
          {nachBereich.map((bereich) => (
            <Karte key={bereich.bereich}>
              <h3 className="mb-2 text-lg font-bold">
                {beschriftung(bereich.name_i18n, i18n.language) || bereich.bereich}
              </h3>
              {bereich.gruppen.map((block, i) => (
                <div key={i}>
                  {beschriftung(block.gruppe_i18n, i18n.language) && (
                    <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-leise">
                      {beschriftung(block.gruppe_i18n, i18n.language)}
                    </div>
                  )}
                <ul className="divide-y divide-linie">
                  {block.points.map((p) => (
                    <li key={p.catalog_id} className="py-2">
                      {/*
                        Beschriftung und Schaltflaechen stehen in zwei festen
                        Spalten. Vorher liefen die Schaltflaechen bei langen
                        Beschriftungen in die naechste Zeile und die Liste
                        wirkte zerrissen.
                      */}
                      <div className="flex items-start justify-between gap-3">
                        <span className="flex min-w-0 flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">
                            {beschriftung(p.label_i18n, i18n.language, p.key)}
                          </span>
                          {p.input_type !== "status" && (
                            <span className="text-xs text-leise">{t(`baukasten.art.${p.input_type}`)}</span>
                          )}
                          {zielbereich(p.target_min, p.target_max, p.unit) && (
                            <span className="rounded bg-grund px-2 py-0.5 text-xs">
                              {zielbereich(p.target_min, p.target_max, p.unit)}
                            </span>
                          )}
                          {p.photo_required && (
                            <span className="rounded bg-grund px-2 py-0.5 text-xs">{t("baukasten.fotoPflicht")}</span>
                          )}
                          {p.herkunft === "objekt" && (
                            <span className="rounded bg-marke/20 px-2 py-0.5 text-xs">{t("checkliste.ergaenzt")}</span>
                          )}
                          {p.angepasst && p.herkunft !== "objekt" && (
                            <span className="rounded bg-marke/20 px-2 py-0.5 text-xs">{t("checkliste.angepasst")}</span>
                          )}
                        </span>
                        {darfAendern && (
                          <span className="flex shrink-0 gap-3">
                            <Textknopf onClick={() => setPunktAnpassen(punktAnpassen === p.catalog_id ? null : p.catalog_id)}>
                              {t("checkliste.anpassen")}
                            </Textknopf>
                            <Textknopf gefahr onClick={() => punktSchalten(p.catalog_id, false)}>
                              {t("checkliste.abwaehlen")}
                            </Textknopf>
                          </span>
                        )}
                      </div>
                      {beschriftung(p.note_i18n, i18n.language) && (
                        <p className="mt-1 text-xs text-leise">
                          {beschriftung(p.note_i18n, i18n.language)}
                        </p>
                      )}
                      {punktAnpassen === p.catalog_id && (
                        <PunktAnpassen
                          checklistId={id}
                          catalogId={p.catalog_id}
                          istZahl={p.input_type === "number"}
                          vorbelegung={{
                            targetMin: p.target_min ?? "",
                            targetMax: p.target_max ?? "",
                            note: beschriftung(p.note_i18n, i18n.language),
                            // null heisst: keine Abweichung, es gilt der Standard.
                            foto: roh.pointOverlays.find((o) => o.catalog_id === p.catalog_id)
                              ?.photo_required ?? null
                          }}
                          sprache={i18n.language}
                          onFertig={() => { setPunktAnpassen(null); laden(); }}
                          onAbbruch={() => setPunktAnpassen(null)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
                </div>
              ))}
            </Karte>
          ))}
        </div>
      )}

      {darfAendern && nachruestbar.length > 0 && (
        <Karte className="mt-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t("checkliste.punktErgaenzen")}</span>
            <span className="mb-2 block text-xs text-leise">{t("checkliste.punktErgaenzenHinweis")}</span>
            <Auswahl
              value=""
              onChange={async (e) => {
                if (!e.target.value) return;
                try {
                  await api.post(`/property-checklists/${id}/points`, {
                    catalogId: e.target.value, action: "add"
                  });
                  laden();
                } catch (err) {
                  setFehler(fehlertext(err, t("fehler.speichern")));
                }
              }}
            >
              <option value="">…</option>
              {nachruestbar.map((p) => (
                <option key={p.id} value={p.id}>
                  {bereichsName(alleBereiche, p.bereich, i18n.language)}
                  {" · "}
                  {beschriftung(p.label_i18n, i18n.language, p.key)}
                </option>
              ))}
            </Auswahl>
          </label>
        </Karte>
      )}

      {abgewaehlt.length > 0 && (
        <Karte className="mt-3">
          <h3 className="mb-2 text-sm font-semibold">{t("checkliste.abgewaehltePunkte")}</h3>
          <ul className="space-y-1">
            {abgewaehlt.map((o) => (
              <li key={o.catalog_id} className="flex items-center gap-2 text-sm text-leise">
                <span className="line-through">{beschriftung(o.label_i18n, i18n.language, o.key)}</span>
                {darfAendern && (
                  <Textknopf onClick={() => punktSchalten(o.catalog_id, true)}>
                    {t("checkliste.wiederAufnehmen")}
                  </Textknopf>
                )}
              </li>
            ))}
          </ul>
        </Karte>
      )}
    </>
  );
}

function PunktAnpassen({
  checklistId, catalogId, istZahl, vorbelegung, sprache, onFertig, onAbbruch
}: {
  checklistId: string;
  catalogId: string;
  istZahl: boolean;
  vorbelegung: { targetMin: string; targetMax: string; note: string; foto: boolean | null };
  sprache: string;
  onFertig: () => void;
  onAbbruch: () => void;
}) {
  const { t } = useTranslation();
  const [min, setMin] = useState(vorbelegung.targetMin ? String(Number(vorbelegung.targetMin)) : "");
  const [max, setMax] = useState(vorbelegung.targetMax ? String(Number(vorbelegung.targetMax)) : "");
  const [note, setNote] = useState(vorbelegung.note);
  /*
   * Drei Zustaende, nicht zwei.
   *
   * "wie im Standard" ist etwas anderes als "keine Pflicht": beim ersten folgt
   * das Objekt automatisch, wenn der Standard sich aendert, beim zweiten
   * ausdruecklich nicht. Ein einfacher Haken koennte das nicht ausdruecken und
   * wuerde beim ersten Speichern stillschweigend eine Abweichung anlegen, die
   * niemand gewollt hat.
   */
  const [foto, setFoto] = useState(
    vorbelegung.foto === null ? "standard" : vorbelegung.foto ? "pflicht" : "frei"
  );
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern() {
    setFehler(null);
    try {
      await api.post(`/property-checklists/${checklistId}/points`, {
        catalogId,
        action: "override",
        targetMin: istZahl ? min : "",
        targetMax: istZahl ? max : "",
        // Fehlt das Feld ganz, traegt die API nichts ein und der Standard gilt.
        ...(foto === "standard" ? {} : { photoRequired: foto === "pflicht" }),
        noteI18n: note.trim() ? { [sprache]: note.trim() } : {}
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <div className="mt-3 rounded border border-marke/40 bg-grund/50 p-3">
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        {istZahl && (
          <>
            <Feld label={t("baukasten.zielVon")}>
              <Eingabe inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
            </Feld>
            <Feld label={t("baukasten.zielBis")}>
              <Eingabe inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
            </Feld>
          </>
        )}
        <Feld label={t("baukasten.fotoPflicht")} hinweis={t("checkliste.fotoHinweis")}>
          <Auswahl value={foto} onChange={(e) => setFoto(e.target.value)}>
            <option value="standard">{t("checkliste.fotoStandard")}</option>
            <option value="pflicht">{t("checkliste.fotoPflicht")}</option>
            <option value="frei">{t("checkliste.fotoFrei")}</option>
          </Auswahl>
        </Feld>
        <Feld label={t("checkliste.objekthinweis")} hinweis={t("checkliste.objekthinweisHinweis")} breit>
          <Eingabe value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={t("checkliste.objekthinweisBeispiel")} />
        </Feld>
      </div>
      <div className="mt-3 flex gap-2">
        <Knopf onClick={speichern}>{t("aktion.speichern")}</Knopf>
        <Knopf art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
        <button
          className="ms-auto text-xs text-leise underline"
          onClick={async () => {
            await api.del(`/property-checklists/${checklistId}/points/${catalogId}`);
            onFertig();
          }}
        >
          {t("checkliste.anpassungZuruecknehmen")}
        </button>
      </div>
    </div>
  );
}

/** Die Checklisten eines Objekts. Wird auf der Objektseite eingebunden. */
export function ChecklistenAmObjekt({ propertyId, darfAendern }: { propertyId: string; darfAendern: boolean }) {
  const { t, i18n } = useTranslation();
  const [listen, setListen] = useState<
    { id: string; label: string | null; bereiche: Bereich[]; is_active: boolean }[] | null
  >(null);
  const [neu, setNeu] = useState(false);
  const [bezeichnung, setBezeichnung] = useState("");
  const { bereiche: alleBereiche } = useBereiche();
  const [bereiche, setBereiche] = useState<Bereich[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  /*
   * Vorgabe fuer eine neue Liste sind alle aktiven Bereiche.
   *
   * Der Grundsatz von 0031 gilt weiter: lieber ein Punkt zu viel, der in zehn
   * Sekunden abgewaehlt ist, als einer, der still fehlt. Welche Bereiche ein
   * Betrieb ueberhaupt fuehrt, entscheidet er im Baukasten, und was er dort
   * angelegt hat, will er in aller Regel auch pruefen.
   */
  const vorgabe = (alleBereiche ?? []).filter((b) => b.is_active).map((b) => b.key);

  function laden() {
    void api.get<{ checklists: typeof listen }>(`/properties/${propertyId}/checklists`)
      .then((r) => setListen(r.checklists ?? []))
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [propertyId]);

  async function anlegen() {
    setFehler(null);
    try {
      await api.post(`/properties/${propertyId}/checklists`, { label: bezeichnung, bereiche });
      setNeu(false); setBezeichnung(""); setBereiche(vorgabe);
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte className="lg:col-span-2">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-bold">{t("checkliste.titel")}</h2>
        {darfAendern && !neu && (
          <Knopf art="neben" onClick={() => { setBereiche(vorgabe); setNeu(true); }}>
            {t("checkliste.hinzufuegen")}
          </Knopf>
        )}
      </div>
      <Fehler text={fehler} />

      {neu && (
        <div className="mb-4 rounded border border-marke/40 bg-grund/40 p-3">
          <Feld label={t("checkliste.bezeichnung")} hinweis={t("checkliste.bezeichnungHinweis")}>
            <Eingabe value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} placeholder="Wöchentlich" />
          </Feld>
          <div className="mt-3">
            <span className="mb-2 block text-sm font-medium">{t("checkliste.bereiche")}</span>
            <div className="flex flex-wrap gap-4">
              {(alleBereiche ?? []).filter((b) => b.is_active).map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 accent-marke"
                    checked={bereiche.includes(b.key)}
                    onChange={(e) =>
                      setBereiche(e.target.checked ? [...bereiche, b.key] : bereiche.filter((x) => x !== b.key))}
                  />
                  {beschriftung(b.name_i18n, i18n.language, b.key)}
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Knopf onClick={anlegen} disabled={bereiche.length === 0}>{t("aktion.speichern")}</Knopf>
            <Knopf art="neben" onClick={() => setNeu(false)}>{t("aktion.abbrechen")}</Knopf>
          </div>
        </div>
      )}

      {!listen ? <Laedt /> : listen.length === 0 ? (
        <p className="text-sm text-leise">{t("checkliste.keine")}</p>
      ) : (
        <ul className="space-y-2">
          {listen.map((l) => (
            <li key={l.id}>
              <Link to={`/checklisten/${l.id}`}
                className="flex flex-wrap items-center gap-2 rounded px-2 py-2 hover:bg-grund">
                <span className="font-semibold">{l.label || t("checkliste.ohneBezeichnung")}</span>
                <span className="text-sm text-leise">
                  {l.bereiche.map((b) => bereichsName(alleBereiche, b, i18n.language)).join(", ")}
                </span>
                {!l.is_active && <span className="text-xs text-leise">{t("baukasten.stillgelegt")}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Karte>
  );
}
